import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  QueueListIcon,
  ArrowUturnLeftIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Conversation } from '../../../types/conversation';
import {
  CHANNEL_TYPE_LABELS,
  CONVERSATION_STATUS_LABELS,
  ConversationStatus,
  SupportMessageType,
} from '../../../types/atendimentoEnums';
import { WorkspaceView } from '../../../types/workspaceView';
import { WorkspaceThemeClasses } from './workspaceTheme';
import { WorkspaceQueueScope } from './useWorkspaceConversations';
import { WorkspaceViewSelector } from './WorkspaceViewSelector';
import { WorkspaceInboxFilter, WorkspaceInboxFilterValue } from './WorkspaceInboxFilter';
import { Inbox } from '../../../types/inbox';
import { ChannelTypeIcon } from '../ChannelTypeIcon';
import { ClassificationBadges } from './ClassificationBadges';
import { resolveClassificationBadges } from '../../../utils/classificationBadgeDisplay';
import { formatQueuePreview, formatUnreadBadge } from '../../../utils/conversationQueuePreview';

interface WorkspaceQueueProps {
  theme: WorkspaceThemeClasses;
  conversations: Conversation[];
  selectedId: string | null;
  loading: boolean;
  accessibleInboxes: Inbox[];
  inboxFilter: WorkspaceInboxFilterValue;
  queueScope: WorkspaceQueueScope;
  activeView: WorkspaceView;
  compact?: boolean;
  onSelect: (id: string) => void;
  onInboxFilterChange: (value: WorkspaceInboxFilterValue) => void;
  onQueueScopeChange: (scope: WorkspaceQueueScope) => void;
  onViewChange: (view: WorkspaceView) => void;
}

const formatRelativeTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime()))
  {
    return '';
  }

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 1)
  {
    return 'agora';
  }

  if (minutes < 60)
  {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24)
  {
    return `${hours} h`;
  }

  return date.toLocaleDateString('pt-BR');
};

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0)
  {
    return '?';
  }

  if (parts.length === 1)
  {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarColor = (name: string): string => {
  const palette = ['#2563eb', '#1d4ed8', '#3b82f6', '#6366f1', '#0ea5e9', '#1e40af'];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1)
  {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return palette[Math.abs(hash) % palette.length];
};

const statusTone = (status: ConversationStatus): string => {
  if (status === ConversationStatus.WaitingForOperator || status === ConversationStatus.Pending)
  {
    return 'bg-amber-100 text-amber-800';
  }

  if (status === ConversationStatus.InProgress)
  {
    return 'bg-blue-100 text-blue-800';
  }

  if (status === ConversationStatus.Resolved)
  {
    return 'bg-gray-200 text-gray-700';
  }

  if (status === ConversationStatus.Captured)
  {
    return 'bg-purple-100 text-purple-800';
  }

  return 'bg-emerald-100 text-emerald-800';
};

const SCOPE_OPTIONS: Array<{
  value: WorkspaceQueueScope;
  label: string;
  hint: string;
  Icon: typeof ChatBubbleLeftRightIcon;
}> = [
  {
    value: 'active',
    label: 'Em andamento',
    hint: 'Abertas e em atendimento',
    Icon: ChatBubbleLeftRightIcon,
  },
  {
    value: 'resolved',
    label: 'Finalizados',
    hint: 'Conversas resolvidas',
    Icon: CheckCircleIcon,
  },
  {
    value: 'captured',
    label: 'Capturados',
    hint: 'Guardadas para depois',
    Icon: ArchiveBoxIcon,
  },
  {
    value: 'all',
    label: 'Todos',
    hint: 'Inclui finalizados e capturados',
    Icon: QueueListIcon,
  },
];

interface WorkspaceQueueFiltersProps {
  theme: WorkspaceThemeClasses;
  queueScope: WorkspaceQueueScope;
  onQueueScopeChange: (scope: WorkspaceQueueScope) => void;
}

const WorkspaceQueueFilters: React.FC<WorkspaceQueueFiltersProps> = ({
  theme,
  queueScope,
  onQueueScopeChange,
}) => {
  const selected = SCOPE_OPTIONS.find((option) => option.value === queueScope) ?? SCOPE_OPTIONS[0];
  const SelectedIcon = selected.Icon;

  return (
    <div>
      <label className={`block text-[10.5px] font-bold uppercase tracking-wide mb-1 ${theme.faint}`}>
        Status
      </label>
      <Listbox value={queueScope} onChange={onQueueScopeChange}>
        <div className="relative">
          <Listbox.Button
            className={`relative w-full cursor-pointer rounded-md border px-2.5 py-1.5 pr-8 text-left text-xs transition-colors ${theme.input}`}
            aria-label="Filtro da fila"
          >
            <span className="flex items-center gap-2 min-w-0">
              <SelectedIcon className={`h-4 w-4 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
              <span className={`block truncate font-medium ${theme.text}`}>{selected.label}</span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className={`h-4 w-4 ${theme.faint}`} aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className={`absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border py-1 text-xs shadow-lg focus:outline-none ${theme.border} ${theme.surface}`}
            >
              {SCOPE_OPTIONS.map((option) => {
                const OptionIcon = option.Icon;

                return (
                  <Listbox.Option key={option.value} value={option.value} as={Fragment}>
                    {({ active, selected: isSelected }) => (
                      <li
                        className={`relative flex cursor-pointer select-none items-start gap-2 px-3 py-2 ${
                          active ? theme.selectedRow : ''
                        }`}
                      >
                        <OptionIcon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-medium'} ${theme.text}`}>
                            {option.label}
                          </span>
                          <span className={`block truncate text-[10.5px] ${theme.muted}`}>
                            {option.hint}
                          </span>
                        </span>
                        {isSelected && (
                          <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
                        )}
                      </li>
                    )}
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export const WorkspaceQueue: React.FC<WorkspaceQueueProps> = ({
  theme,
  conversations,
  selectedId,
  loading,
  accessibleInboxes,
  inboxFilter,
  queueScope,
  activeView,
  compact = false,
  onSelect,
  onInboxFilterChange,
  onQueueScopeChange,
  onViewChange,
}) => {
  const emptyMessage =
    queueScope === 'resolved'
      ? 'Nenhum atendimento finalizado encontrado.'
      : queueScope === 'captured'
        ? 'Nenhuma conversa capturada encontrada.'
        : queueScope === 'all'
          ? 'Nenhuma conversa encontrada.'
          : 'Nenhuma conversa em andamento.';

  const unreadTotal = conversations.reduce((sum, conversation) => sum + (conversation.unreadCount ?? 0), 0);
  const headerPillValue = unreadTotal > 0 ? unreadTotal : conversations.length;
  const headerPillClass = unreadTotal > 0
    ? 'bg-blue-600 text-white'
    : theme.badge;

  return (
    <aside className={`flex-col overflow-hidden w-full lg:flex-shrink-0 ${compact ? 'lg:w-[270px]' : 'lg:w-[320px]'} ${theme.surface} lg:border-r ${theme.border} ${selectedId ? 'hidden lg:flex' : 'flex'}`}>
      <div className={`px-3.5 py-3 border-b ${theme.border}`}>
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <h2 className={`text-sm font-bold ${theme.text}`}>Fila de atendimento</h2>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${headerPillClass}`}>
            {headerPillValue}
          </span>
        </div>

        <WorkspaceQueueFilters
          theme={theme}
          queueScope={queueScope}
          onQueueScopeChange={onQueueScopeChange}
        />

        <WorkspaceViewSelector
          theme={theme}
          activeView={activeView}
          onViewChange={onViewChange}
        />

        <WorkspaceInboxFilter
          theme={theme}
          inboxes={accessibleInboxes}
          value={inboxFilter}
          onChange={onInboxFilterChange}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          </div>
        ) : conversations.length === 0 ? (
          <div className={`p-6 text-center text-sm ${theme.muted}`}>
            {emptyMessage}
          </div>
        ) : (
          conversations.map((conversation) => {
            const isSelected = conversation.id === selectedId;
            const isResolved = conversation.status === ConversationStatus.Resolved;
            const channelLabel = CHANNEL_TYPE_LABELS[conversation.channelType] ?? 'Canal';
            const classificationBadges = resolveClassificationBadges(conversation);
            const unreadCount = conversation.unreadCount ?? 0;
            const hasUnread = unreadCount > 0;
            const preview = formatQueuePreview(conversation.lastNonActivityMessage?.preview);
            const previewMessage = conversation.lastNonActivityMessage;

            return (
              <button
                key={conversation.id}
                type="button"
                onClick={() => onSelect(conversation.id)}
                className={`relative w-full text-left grid grid-cols-[auto_1fr_auto] gap-2.5 px-3.5 py-3 border-b transition-colors ${theme.border} ${
                  isSelected ? theme.selectedRow : 'hover:opacity-90'
                } ${isResolved ? 'opacity-75' : ''}`}
              >
                {isSelected && (
                  <span className={`absolute left-0 top-0 bottom-0 w-0.5 ${theme.selectedBar}`} />
                )}
                {isResolved && !isSelected && (
                  <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-300" />
                )}
                <div
                  className={`h-9 w-9 rounded-full grid place-items-center text-xs font-bold text-white flex-shrink-0 ${
                    isResolved ? 'grayscale' : ''
                  }`}
                  style={{ backgroundColor: avatarColor(conversation.contactName) }}
                >
                  {initialsFromName(conversation.contactName)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className={`text-[13px] truncate ${hasUnread ? 'font-semibold' : 'font-medium'} ${theme.text}`}>
                      {conversation.contactName}
                    </p>
                    {isResolved && (
                      <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                        Finalizado
                      </span>
                    )}
                    {classificationBadges.length > 0 && (
                      <ClassificationBadges badges={classificationBadges} compact />
                    )}
                  </div>
                  <p className={`text-xs truncate mt-0.5 flex items-center gap-1 ${
                    hasUnread ? `font-medium ${theme.text}` : theme.muted
                  }`}>
                    {previewMessage?.isPrivate && (
                      <LockClosedIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    )}
                    {!previewMessage?.isPrivate && previewMessage?.messageType === SupportMessageType.Outgoing && (
                      <ArrowUturnLeftIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    )}
                    <span className="truncate">{preview}</span>
                  </p>
                  {!compact && (
                    <p className={`text-[11px] truncate mt-0.5 ${theme.faint}`}>
                      #{conversation.displayId} · {conversation.inboxName}
                      {conversation.supportGroupName ? ` · ${conversation.supportGroupName}` : ''}
                    </p>
                  )}
                  {!compact && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] ${theme.faint}`}>
                        <ChannelTypeIcon channelType={conversation.channelType} className="h-3.5 w-3.5" />
                        {channelLabel}
                      </span>
                      {!isResolved && (
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${statusTone(conversation.status)}`}>
                          {CONVERSATION_STATUS_LABELS[conversation.status]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`text-[11px] whitespace-nowrap ${theme.faint}`}>
                    {formatRelativeTime(conversation.lastActivityAt)}
                  </span>
                  {hasUnread && (
                    <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold grid place-items-center">
                      {formatUnreadBadge(unreadCount)}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
};
